import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { ColumnDef, Row } from '@/components/UI/dataTableConfig';
import { Button, Card, EmptyState, ExpandableDataTable } from '@/components/UI';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import { trpc, trpcClient } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import RuleGroupSubTable from './RuleGroupSubTable';
import { ruleRoleOptions } from './constants';
import { getRuleRoleClassName } from './helpers';
import type { RuleGroupRow, ScorecardRuleRole, ScorecardRuleRow } from './types';

interface RulesCardProps {
    rules: ScorecardRuleRow[];
    setRules: React.Dispatch<React.SetStateAction<ScorecardRuleRow[]>>;
    loadRules: () => Promise<void>;
    getRuleFeatureLabel: (rule: Pick<ScorecardRuleRow, 'featureKey' | 'display'>) => string;
    getRuleSecondaryFeatureLabel: (rule: Pick<ScorecardRuleRow, 'featureKey' | 'display'>) => string | null;
}

const RulesCard = ({ rules, setRules, loadRules, getRuleFeatureLabel, getRuleSecondaryFeatureLabel }: RulesCardProps) => {
    const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [ruleToDelete, setRuleToDelete] = useState<ScorecardRuleRow | null>(null);
    // Bulk delete (group)
    const [confirmBulkOpen, setConfirmBulkOpen] = useState<boolean>(false);
    const [groupToDelete, setGroupToDelete] = useState<RuleGroupRow | null>(null);
    const [bulkDeleting, setBulkDeleting] = useState<boolean>(false);

    // Inline rule updates
    const updateRuleMutation = useTrpcMutation(trpc.scorecards.updateRule.mutationOptions({}), {
        successMessage: 'Rule updated',
        fallbackErrorMessage: 'Failed to update rule'
    });

    const onToggleRuleEnabled = async (ruleId: number, enabled: boolean) => {
        try {
            await updateRuleMutation.mutateAsync({ ruleId, enabled });

            setRules((prev) =>
                prev.map((r) =>
                    r.id === ruleId ? { ...r, enabled: enabled ? 1 : 0, updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss') } : r
                )
            );
        } catch (e) {
            // toast handled by hook
            console.error(e);
        }
    };

    const onUpdateRuleRole = async (ruleId: number, ruleRole: ScorecardRuleRole) => {
        try {
            await updateRuleMutation.mutateAsync({ ruleId, ruleRole });

            setRules((prev) =>
                prev.map((r) =>
                    r.id === ruleId
                        ? {
                              ...r,
                              ruleRole,
                              isVetoRule: ruleRole === 'veto' ? 1 : 0,
                              updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss')
                          }
                        : r
                )
            );
        } catch (e) {
            // toast handled by hook
            console.error(e);
        }
    };

    // Grouped rules view
    const [groupedView, setGroupedView] = useState<boolean>(true);

    const ruleGroups: RuleGroupRow[] = useMemo(() => {
        const byKey = new Map<string, RuleGroupRow>();

        for (const r of rules) {
            const key = r.serializedContextFeatureRef ?? r.featureKey ?? String(r.id);
            const existing = byKey.get(key);
            const updatedAt = r.updatedAt || '';

            if (!existing) {
                byKey.set(key, {
                    key,
                    pathLabel: getRuleFeatureLabel(r),
                    secondaryPathLabel: getRuleSecondaryFeatureLabel(r) ?? key,
                    total: 1,
                    enabled: r.enabled ? 1 : 0,
                    selectorTypes: [r.selectorType],
                    latestUpdatedAt: updatedAt,
                    rules: [r]
                });
            } else {
                existing.total += 1;
                existing.enabled += r.enabled ? 1 : 0;
                if (!existing.selectorTypes.includes(r.selectorType)) existing.selectorTypes.push(r.selectorType);
                // pick latest by string compare or replace
                if (String(updatedAt) > String(existing.latestUpdatedAt)) existing.latestUpdatedAt = updatedAt;
                existing.rules.push(r);
            }
        }

        return Array.from(byKey.values());
    }, [getRuleFeatureLabel, getRuleSecondaryFeatureLabel, rules]);

    const groupColumns: ColumnDef<RuleGroupRow>[] = useMemo(
        () => [
            {
                header: 'Parameter',
                accessorKey: 'pathLabel',
                size: 360,
                cell: ({ row }) => (
                    <div>
                        <div>{row.original.pathLabel}</div>
                        {row.original.secondaryPathLabel && (
                            <div className="text-xs text-zinc-500 break-all">{row.original.secondaryPathLabel}</div>
                        )}
                    </div>
                )
            },
            {
                header: 'Counts',
                accessorKey: 'total',
                size: 120,
                cell: ({ row }) => {
                    const g = row.original;
                    return (
                        <span className="text-zinc-200">
                            {g.enabled}/{g.total} enabled
                        </span>
                    );
                }
            },
            {
                header: 'Selector Types',
                accessorKey: 'selectorTypes',
                size: 220,
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-1">
                        {row.original.selectorTypes.map((t) => (
                            <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-300" key={t}>
                                {t}
                            </span>
                        ))}
                    </div>
                )
            },
            {
                header: 'Updated',
                accessorKey: 'latestUpdatedAt',
                size: 180,
                cell: ({ row }) => {
                    const d = row.original.latestUpdatedAt;
                    return d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '';
                }
            },
            {
                header: 'Actions',
                id: 'actions',
                size: 240,
                cell: ({ row }) => {
                    const g = row.original;

                    const toggleAll = async (enabled: boolean) => {
                        if (!g.rules.length) return;

                        for (const r of g.rules) {
                            // Skip if already in desired state
                            if ((r.enabled ? 1 : 0) === (enabled ? 1 : 0)) continue;

                            await updateRuleMutation.mutateAsync({ ruleId: r.id, enabled });

                            setRules((prev) =>
                                prev.map((it) =>
                                    it.id === r.id
                                        ? { ...it, enabled: enabled ? 1 : 0, updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss') }
                                        : it
                                )
                            );
                        }
                    };

                    return (
                        <div className="flex gap-2 items-center">
                            <Button disabled={updateRuleMutation.isPending} size="sm" variant="secondary" onClick={() => toggleAll(true)}>
                                Enable All
                            </Button>
                            <Button disabled={updateRuleMutation.isPending} size="sm" variant="secondary" onClick={() => toggleAll(false)}>
                                Disable All
                            </Button>
                            <Button
                                disabled={bulkDeleting}
                                size="sm"
                                variant="danger"
                                onClick={() => {
                                    setGroupToDelete(g);
                                    setConfirmBulkOpen(true);
                                }}
                            >
                                Delete All
                            </Button>
                        </div>
                    );
                }
            }
        ],
        [updateRuleMutation]
    );

    const renderGroupSubComponent = ({ row }: { row: Row<RuleGroupRow> }) => {
        const g = row.original;

        return (
            <RuleGroupSubTable
                deletingRuleId={deletingRuleId}
                group={g}
                onRequestDelete={(rule) => {
                    setRuleToDelete(rule);
                    setConfirmOpen(true);
                }}
                onToggleRuleEnabled={onToggleRuleEnabled}
                onUpdateRuleRole={onUpdateRuleRole}
            />
        );
    };

    return (
        <>
            <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white text-lg font-semibold">Rules</h3>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-zinc-300 flex items-center gap-2">
                            <input
                                checked={groupedView}
                                className="cursor-pointer"
                                type="checkbox"
                                onChange={(e) => setGroupedView(e.target.checked)}
                            />
                            Group by parameter
                        </label>
                        <Button variant="secondary" onClick={() => loadRules()}>
                            Refresh
                        </Button>
                    </div>
                </div>

                {rules.length === 0 ? (
                    <EmptyState description="Use Parameter Discovery to add rules to this scorecard." size="sm" title="No rules yet." />
                ) : groupedView ? (
                    <ExpandableDataTable
                        allowOverflow
                        search
                        sort
                        striped
                        columns={groupColumns}
                        data={ruleGroups}
                        pagination={{ enabled: ruleGroups.length > 12, pageSize: 12 }}
                        renderSubComponent={renderGroupSubComponent}
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-zinc-300">
                                    <th className="text-left p-2">ID</th>
                                    <th className="text-left p-2">Enabled</th>
                                    <th className="text-left p-2">Role</th>
                                    <th className="text-left p-2">Parameter</th>
                                    <th className="text-left p-2">Selector</th>
                                    <th className="text-left p-2">Lifts (Stock/Option %)</th>
                                    <th className="text-left p-2">Sample Size</th>
                                    <th className="text-left p-2">Updated</th>
                                    <th className="text-left p-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rules.map((r) => {
                                    let selectorStr: string;

                                    try {
                                        selectorStr = r.selector ? JSON.stringify(JSON.parse(r.selector)) : '';
                                    } catch {
                                        selectorStr = r.selector || '';
                                    }

                                    let metrics: string;

                                    try {
                                        const parts: string[] = [];
                                        if (r.stockLift) parts.push(`stock ${Number(r.stockLift).toFixed(2)}%`);
                                        if (r.optionLift) parts.push(`option ${Number(r.optionLift).toFixed(2)}%`);
                                        metrics = parts.join(' | ');
                                    } catch {
                                        metrics = '';
                                    }

                                    const secondaryRuleFeatureLabel = getRuleSecondaryFeatureLabel(r);

                                    return (
                                        <tr className="border-t border-zinc-800 text-zinc-200" key={r.id}>
                                            <td className="p-2">{r.id}</td>
                                            <td className="p-2">
                                                <input
                                                    aria-label={`Toggle rule ${r.id}`}
                                                    checked={!!r.enabled}
                                                    className="cursor-pointer"
                                                    type="checkbox"
                                                    onChange={(e) => onToggleRuleEnabled(r.id, e.target.checked)}
                                                />
                                            </td>
                                            <td className="p-2">
                                                <select
                                                    aria-label={`Rule ${r.id} role`}
                                                    className={`rounded border px-2 py-1 text-xs ${getRuleRoleClassName(r.ruleRole)}`}
                                                    value={r.ruleRole}
                                                    onChange={(event) => onUpdateRuleRole(r.id, event.target.value as ScorecardRuleRole)}
                                                >
                                                    {ruleRoleOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-2">
                                                <div>{getRuleFeatureLabel(r)}</div>
                                                {secondaryRuleFeatureLabel && (
                                                    <div className="text-xs text-zinc-500">{secondaryRuleFeatureLabel}</div>
                                                )}
                                            </td>
                                            <td className="p-2 font-mono text-xs break-all max-w-[360px]">{selectorStr}</td>
                                            <td className="p-2">{metrics}</td>
                                            <td className="p-2">{r.sampleSize}</td>
                                            <td className="p-2">{r.updatedAt ? dayjs(r.updatedAt).format('YYYY-MM-DD HH:mm') : ''}</td>
                                            <td className="p-2">
                                                <Button
                                                    disabled={deletingRuleId === r.id}
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => {
                                                        setRuleToDelete(r);
                                                        setConfirmOpen(true);
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <ConfirmationDialog
                confirmText={deletingRuleId && deletingRuleId === ruleToDelete?.id ? 'Removing...' : 'Remove'}
                confirmVariant="danger"
                isOpen={confirmOpen}
                message={`Are you sure you want to remove rule #${ruleToDelete?.id ?? ''}?`}
                title="Remove Rule"
                onCancel={() => {
                    if (deletingRuleId) return; // prevent closing mid-flight
                    setConfirmOpen(false);
                    setRuleToDelete(null);
                }}
                onConfirm={async () => {
                    if (!ruleToDelete) return;

                    try {
                        setDeletingRuleId(ruleToDelete.id);
                        await trpcClient.scorecards.deleteRule.mutate({ ruleId: ruleToDelete.id });
                        showToast('Rule removed', 'success');
                        await loadRules();
                        setConfirmOpen(false);
                        setRuleToDelete(null);
                    } catch (err) {
                        const error = err as Error;
                        showToast(error?.message || 'Failed to remove rule', 'error');
                    } finally {
                        setDeletingRuleId(null);
                    }
                }}
            />

            {/* Bulk delete confirmation */}
            <ConfirmationDialog
                confirmText={bulkDeleting ? 'Deleting…' : 'Delete All'}
                confirmVariant="danger"
                isOpen={confirmBulkOpen}
                message={
                    groupToDelete
                        ? `Delete all ${groupToDelete.rules.length} rules for “${groupToDelete.pathLabel}”? This cannot be undone.`
                        : ''
                }
                title="Delete All Rules in Group"
                onCancel={() => {
                    if (bulkDeleting) return;
                    setConfirmBulkOpen(false);
                    setGroupToDelete(null);
                }}
                onConfirm={async () => {
                    if (!groupToDelete) return;

                    try {
                        setBulkDeleting(true);

                        // Delete sequentially to avoid overwhelming the server and ease recovery
                        for (const r of groupToDelete.rules) {
                            await trpcClient.scorecards.deleteRule.mutate({ ruleId: r.id });
                        }

                        showToast('All rules in group deleted', 'success');
                        await loadRules();
                        setConfirmBulkOpen(false);
                        setGroupToDelete(null);
                    } catch (err) {
                        const error = err as Error;
                        showToast(error?.message || 'Failed to delete rules', 'error');
                    } finally {
                        setBulkDeleting(false);
                    }
                }}
            />
        </>
    );
};

export default RulesCard;
